import React, { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FolderPayload, FoldersProvider } from '@opengov/bnp-component-folders/src';
import { useGetReportsFolders } from '../../../../../src/pages/reportsPage/components/ReportsFolders/hooks/useGetReportsFolders/useGetReportsFolders';
import { useCreateReportFolder } from '../../../../../src/pages/reportsPage/components/ReportsFolders/hooks/useCreateReportFolder/useCreateReportFolder';
import { reportsFoldersQueryKeys } from '../../../../../src/pages/reportsPage/components/ReportsFolders/hooks/useGetReportsFolders/reportsFolders.queryKeys';
import { getRootFolder } from '../../../../../src/pages/reportsPage/components/ReportsFolders/ReportsFolders.utils';

type CopyReportFoldersProviderProps = {
  entityId: number;
  children: React.ReactNode;
};

/**
 * Wraps children with a FoldersProvider populated with the current entity's
 * report folders, enabling folder selection / creation inside report-level
 * dialogs (e.g. Duplicate Report) which live outside the Reports page route
 * tree.
 */
export const CopyReportFoldersProvider = ({ entityId, children }: CopyReportFoldersProviderProps) => {
  const queryClient = useQueryClient();
  const { data: reportsFoldersData } = useGetReportsFolders({
    entityId,
    enabled: Boolean(entityId)
  });
  const { mutateAsync: createReportFolder } = useCreateReportFolder();

  const folders = useMemo(() => reportsFoldersData?.data ?? [], [reportsFoldersData]);
  const rootFolder = useMemo(() => getRootFolder(folders), [folders]);

  const handleCreateFolder = async (folderPayload: FolderPayload) => {
    if (!entityId) {
      return '';
    }
    const response = await createReportFolder({ entityId, ...folderPayload });
    await queryClient.invalidateQueries(reportsFoldersQueryKeys.all);
    return `${response.data.id}`;
  };

  return (
    <FoldersProvider
      rootFolder={rootFolder}
      folderAbilities={{ create: true, duplicate: true, move: true, rename: true, delete: true }}
      createFolder={handleCreateFolder}
    >
      {children}
    </FoldersProvider>
  );
};
